import { MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE } from "@/constants";
import { BalanceType } from "@/types/user";
import { isStripeMinimumChargeApplied } from "@/utils/payment";
import { toLocaleString } from "@/utils/number";

type CreditsBalances =
  | {
      [BalanceType.CREDITS]?: number;
      [BalanceType.CREDITS_WITHDRAWABLE]?: number;
      [BalanceType.CREDITS_COURTESY]?: number;
    }
  | null
  | undefined;

export type CreditsTopUpPaymentMethod = "stripe" | "usdc";

export type CreditsPaymentBreakdown = {
  totalCost: number;
  promotionalBalance: number;
  purchasedCreditsBalance: number;
  /** Courtesy (non-withdrawable, floor-exempt) balance available for this booking. */
  courtesyCreditsBalance: number;
  /** Courtesy credits applied to this booking (drawn down before promo/paid). */
  courtesyApplied: number;
  /** Promo credits applied to this booking (capped by max promo share). */
  promoApplied: number;
  /** Paid credits applied to this booking. */
  purchasedApplied: number;
  /** Additional paid credits required (raw shortfall before Stripe floor). */
  additionalRequired: number;
  /**
   * When paying the shortfall with card under the Stripe floor, surplus above shortfall
   * that is charged to satisfy the minimum and credited for future use.
   */
  stripeMinimumSurplus: number;
};

type CreditsPaymentOptions = {
  totalCost: number;
  creditsBalance: number;
  purchasedCreditsBalance: number;
  minNonPromotionalCreditsRequired?: number;
  /**
   * Courtesy (gifted, non-withdrawable) balance. Already included in `creditsBalance`.
   * Applied before promo/paid and reduces the paid (non-promo) floor dollar-for-dollar,
   * so a booking fully covered by courtesy is exempt from the paid floor.
   */
  courtesyCreditsBalance?: number;
};

const roundMoney = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Paid (non-promo) floor after courtesy is applied. Courtesy is exempt from the
 * floor: the requirement applies only to the courtesy-uncovered remainder, matching
 * the backend `min_non_promotional_credits_required(cost - courtesy)`. We scale the
 * full-cost floor by remaining/cost so the FE gate agrees with the BE charge.
 * When courtesy covers the whole cost the floor drops to 0 (fully exempt).
 */
export const getEffectivePaidFloor = (
  minNonPromotionalCreditsRequired: number,
  totalCost: number,
  courtesyApplied: number
): number => {
  const minRequired = Math.max(0, Number(minNonPromotionalCreditsRequired) || 0);
  const cost = Math.max(0, Number(totalCost) || 0);
  if (minRequired <= 0 || cost <= 0) {
    return 0;
  }
  const remaining = Math.max(0, cost - Math.max(0, courtesyApplied));
  return roundMoney((minRequired * remaining) / cost);
};

/**
 * Raw shortfall before any payment-link minimum is applied.
 * Covers total cost shortfall and paid (non-promo) floor shortfall.
 */
export const getCreditsTopUpShortfall = ({
  totalCost,
  creditsBalance,
  purchasedCreditsBalance,
  minNonPromotionalCreditsRequired = 0,
  courtesyCreditsBalance = 0
}: CreditsPaymentOptions): number => {
  const topUpForTotal = Math.max(0, totalCost - creditsBalance);
  const courtesyApplied = Math.min(
    Math.max(0, Number(courtesyCreditsBalance) || 0),
    Math.max(0, totalCost)
  );
  const effectiveFloor = getEffectivePaidFloor(
    minNonPromotionalCreditsRequired,
    totalCost,
    courtesyApplied
  );
  const topUpForPurchased =
    effectiveFloor > 0 ? Math.max(0, effectiveFloor - purchasedCreditsBalance) : 0;
  return roundMoney(Math.max(topUpForTotal, topUpForPurchased));
};

/**
 * Receipt-style breakdown of total cost, applied promo / paid credits, and shortfall.
 * `maxPromo` uses `minNonPromotionalCreditsRequired` as the paid floor (e.g. 70% → 30% promo).
 */
export const getCreditsPaymentBreakdown = ({
  totalCost,
  creditsBalance,
  purchasedCreditsBalance,
  minNonPromotionalCreditsRequired = 0,
  courtesyCreditsBalance = 0,
  topUpPaymentMethod
}: CreditsPaymentOptions & {
  topUpPaymentMethod?: CreditsTopUpPaymentMethod;
}): CreditsPaymentBreakdown => {
  const cost = roundMoney(Math.max(0, Number(totalCost) || 0));
  const purchased = roundMoney(Math.max(0, Number(purchasedCreditsBalance) || 0));
  const spendable = roundMoney(Math.max(0, Number(creditsBalance) || 0));
  const courtesy = roundMoney(Math.max(0, Number(courtesyCreditsBalance) || 0));
  // Courtesy is a subset of spendable credits and is non-withdrawable, so exclude it
  // (along with paid) from the derived promotional balance.
  const promotionalBalance = roundMoney(Math.max(0, spendable - purchased - courtesy));

  // Courtesy is drawn down first; the remaining cost is split across promo/paid.
  const courtesyApplied = roundMoney(Math.min(courtesy, cost));
  const remainingCost = roundMoney(Math.max(0, cost - courtesyApplied));
  const effectiveFloor = getEffectivePaidFloor(
    minNonPromotionalCreditsRequired,
    cost,
    courtesyApplied
  );
  const maxPromo =
    effectiveFloor > 0 ? roundMoney(Math.max(0, remainingCost - effectiveFloor)) : remainingCost;

  const promoApplied = roundMoney(Math.min(promotionalBalance, maxPromo));
  const purchasedApplied = roundMoney(
    Math.min(purchased, Math.max(0, remainingCost - promoApplied))
  );
  const additionalRequired = roundMoney(
    Math.max(0, remainingCost - promoApplied - purchasedApplied)
  );

  const stripeMinimumSurplus =
    topUpPaymentMethod === "stripe" &&
    additionalRequired > 0 &&
    isStripeMinimumChargeApplied(additionalRequired)
      ? roundMoney(Math.max(0, MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE - additionalRequired))
      : 0;

  return {
    totalCost: cost,
    promotionalBalance,
    purchasedCreditsBalance: purchased,
    courtesyCreditsBalance: courtesy,
    courtesyApplied,
    promoApplied,
    purchasedApplied,
    additionalRequired,
    stripeMinimumSurplus
  };
};

/**
 * How much the user must top up (purchase) before a credits charge can succeed.
 * Covers both total cost shortfall and paid (non-promo) floor shortfall.
 *
 * TEMP: $10 payment-link floor disabled for testing. Restore with:
 *   import { MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE } from "@/constants";
 *   return Math.max(shortfall, MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE);
 */
export const getCreditsTopUpAmount = ({
  totalCost,
  creditsBalance,
  purchasedCreditsBalance,
  minNonPromotionalCreditsRequired = 0,
  courtesyCreditsBalance = 0
}: CreditsPaymentOptions): number => {
  return getCreditsTopUpShortfall({
    totalCost,
    creditsBalance,
    purchasedCreditsBalance,
    minNonPromotionalCreditsRequired,
    courtesyCreditsBalance
  });
};

/**
 * Whether to show the "$10 minimum purchase" notice.
 * TEMP: always false while $10 floor is disabled for testing (USDC path).
 * Card top-up floor is shown via `stripeMinimumSurplus` on the breakdown.
 */
export const isCreditsTopUpMinimumApplied = (_options: CreditsPaymentOptions): boolean => {
  return false;
};

/**
 * True when total spendable credits cover cost and the paid floor is met.
 * Courtesy (included in `creditsBalance`) offsets the paid floor dollar-for-dollar,
 * so a cost fully covered by courtesy needs no paid credits.
 */
export const creditsBalancesSatisfyPayment = ({
  totalCost,
  creditsBalance,
  purchasedCreditsBalance,
  minNonPromotionalCreditsRequired = 0,
  courtesyCreditsBalance = 0
}: CreditsPaymentOptions): boolean => {
  if (totalCost > 0 && creditsBalance < totalCost) {
    return false;
  }
  const courtesyApplied = Math.min(
    Math.max(0, Number(courtesyCreditsBalance) || 0),
    Math.max(0, totalCost)
  );
  const effectiveFloor = getEffectivePaidFloor(
    minNonPromotionalCreditsRequired,
    totalCost,
    courtesyApplied
  );
  if (effectiveFloor > 0 && purchasedCreditsBalance < effectiveFloor) {
    return false;
  }
  return true;
};

export const readCreditsBalances = (balances: CreditsBalances) => {
  const creditsBalance = Number((balances && balances[BalanceType.CREDITS]) || 0);
  const purchasedCreditsBalance = Number(
    (balances && balances[BalanceType.CREDITS_WITHDRAWABLE]) || 0
  );
  const courtesyCreditsBalance = Number((balances && balances[BalanceType.CREDITS_COURTESY]) || 0);
  return { creditsBalance, purchasedCreditsBalance, courtesyCreditsBalance };
};

const DEFAULT_PAID_SHARE_PERCENT = 70;

/** User-facing paid-floor requirement copy for credits deploy/extend. */
export const getPaidCreditsRequirementMessage = ({
  required,
  totalCost
}: {
  required: number;
  totalCost?: number;
}): string => {
  const cost = Number(totalCost) || 0;
  const amount = Math.max(0, Number(required) || 0);
  const pct =
    cost > 0 && amount > 0 ? Math.round((amount / cost) * 100) : DEFAULT_PAID_SHARE_PERCENT;
  return `Deploying requires at least ${pct}% of the cost ($${toLocaleString(
    amount,
    2
  )}) in paid credits.`;
};
